import type { PublicSectionView } from '@/api/types';

export const MODULE_SECTION_PREFIX = 'module:';

export const MODULE_ROUTE_NAMES = [
  'achievement-intro',
  'application-form',
  'summary-report',
  'promotion-effect',
  'support-material',
  'achievement-video',
] as const;

export type ModuleRouteName = (typeof MODULE_ROUTE_NAMES)[number];

export interface ModuleSectionEntry {
  readonly moduleId: string;
  readonly section: PublicSectionView;
}

export interface ModuleTitleParts {
  readonly main: string;
  readonly sub: string | null;
  /** 副标题按半角竖线 `|` 手动断行后的行列表（无标记时单行），供标题下方多行小字渲染 */
  readonly subLines: readonly string[];
}

const MODULE_ID_PATTERN = /^[a-z0-9-]+$/;
const MODULE_ID_ATTRIBUTE_PATTERN = /\sdata-module-id=["']([^"']+)["']/i;
const TRAILING_FULLWIDTH_PAREN_PATTERN = /^(.+?)\s*(（[^（）]*）)$/;
/** 副标题手动断行标记：标题末尾括号说明内用半角竖线 `|` 指定换行位置 */
const TITLE_BREAK_MARKER = '|';
const MODULE_IDS_BY_ROUTE: Record<ModuleRouteName, readonly string[]> = {
  'achievement-intro': ['intro'],
  'application-form': [
    'preview',
    'awards',
    'problem',
    'method',
    'innovation',
    'impact',
  ],
  'summary-report': [
    'summary-report',
    'background',
    'process',
    'problem',
    'method',
    'innovation',
    'impact',
    'outlook',
  ],
  'promotion-effect': [
    'recent-promotion',
    'activity-reports',
    'island-science-tour',
    'interdisciplinary-study-camp',
  ],
  'support-material': ['awards', 'mechanism', 'resources', 'honor', 'social', 'talent'],
  'achievement-video': ['video'],
};

export function isModuleRouteName(value: string): value is ModuleRouteName {
  return (MODULE_ROUTE_NAMES as readonly string[]).includes(value);
}

export function isModuleId(value: string): boolean {
  return MODULE_ID_PATTERN.test(value);
}

/**
 * 把模块标题拆成主标题 + 副标题行，供 banner / 目录 / 正文标题分多行渲染。
 * 两种写法都支持：
 * 1. 末尾全角括号说明（旧写法，如「SNAIL环岛科普（自贸海南 科普有我）」），括号内可用半角竖线 `|` 手动断行；
 * 2. 不带括号，直接用半角竖线 `|` 断行（`|` 前为主标题，`|` 后为小字副标题行，如「近期活动报道《中国青年报》报道|《海南日报》报道」）。
 * 无 `|` 无括号的标题整行作主标题，不渲染副标题。
 */
export function splitModuleTitle(title: string): ModuleTitleParts {
  const trimmed = title.trim();

  // 1) 末尾全角括号说明（旧写法）：括号内支持 | 手动断行
  const matched = TRAILING_FULLWIDTH_PAREN_PATTERN.exec(trimmed);
  if (matched !== null) {
    const sub = matched[2] ?? '';
    const subLines = sub
      .split(TITLE_BREAK_MARKER)
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
    return {
      main: matched[1] ?? trimmed,
      sub,
      subLines: subLines.length > 0 ? subLines : [sub],
    };
  }

  // 2) 无括号：| 前为主标题，| 后按 | 拆成小字副标题行
  const breakIndex = trimmed.indexOf(TITLE_BREAK_MARKER);
  if (breakIndex >= 0) {
    const main = trimmed.slice(0, breakIndex).trim();
    const subLines = trimmed
      .slice(breakIndex + 1)
      .split(TITLE_BREAK_MARKER)
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
    return {
      main: main.length > 0 ? main : trimmed,
      sub: subLines.length > 0 ? subLines.join(TITLE_BREAK_MARKER) : null,
      subLines,
    };
  }

  // 3) 无括号无 |：整行主标题，无副标题
  return { main: trimmed, sub: null, subLines: [] };
}

export function moduleSectionType(routeName: ModuleRouteName): string {
  return `${MODULE_SECTION_PREFIX}${routeName}`;
}

export function moduleIdFromSection(section: PublicSectionView): string | null {
  const matched = MODULE_ID_ATTRIBUTE_PATTERN.exec(section.bodyHtml);
  const value = matched?.[1]?.trim() ?? '';
  return isModuleId(value) ? value : null;
}

function fallbackModuleId(routeName: ModuleRouteName, index: number): string {
  return MODULE_IDS_BY_ROUTE[routeName][index] ?? `module-${index + 1}`;
}

export function moduleSections(
  sections: readonly PublicSectionView[],
  routeName: ModuleRouteName,
): readonly ModuleSectionEntry[] {
  return sections
    .filter((section) => section.type === moduleSectionType(routeName))
    .sort((a, b) => a.sort - b.sort)
    .map((section, index) => ({
      moduleId: moduleIdFromSection(section) ?? fallbackModuleId(routeName, index),
      section,
    }));
}

export function isModuleSectionType(type: string): boolean {
  return MODULE_ROUTE_NAMES.some((routeName) => type === moduleSectionType(routeName));
}

export function moduleRouteLocation(
  routeName: ModuleRouteName,
  moduleId?: string,
  options: { readonly anchor?: string; readonly previewToken?: string | null } = {},
): {
  readonly path: string;
  readonly hash?: string;
  readonly query?: { readonly preview: string };
} {
  const path = moduleId === undefined ? `/${routeName}` : `/${routeName}/${moduleId}`;
  return {
    path,
    hash: options.anchor === undefined ? undefined : `#${options.anchor}`,
    query: options.previewToken ? { preview: options.previewToken } : undefined,
  };
}
